import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2, Tag } from 'lucide-react';
import { communityApi, coursesApi, membershipApi } from '../services/api';
import './PurchasePage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PurchasePage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const couponCode = searchParams.get('coupon') || '';
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Refs to prevent double-execution from React StrictMode
  const productFetchedRef = useRef(false);
  const couponAppliedRef = useRef(false);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('hyperdriveToken');
    setIsLoggedIn(!!token);
  }, []);

  // Fetch product details
  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (productFetchedRef.current) return;
    productFetchedRef.current = true;
    
    const fetchProduct = async () => {
      try {
        setLoading(true);
        if (type === 'membership') {
          const response = await fetch(`${API_URL}/api/community/memberships/${id}`, {
            cache: 'no-store'
          });
          if (!response.ok) throw new Error('Membership not found');
          const data = await response.json();
          setProduct({ ...data, type: 'membership' });
        } else if (type === 'course') {
          const response = await fetch(`${API_URL}/api/courses/${id}`, {
            cache: 'no-store'
          });
          if (!response.ok) throw new Error('Course not found');
          const data = await response.json();
          setProduct({ ...data, type: 'course' });
        } else {
          throw new Error('Invalid product type');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    
    // Reset ref on unmount for remounting scenarios
    return () => {
      productFetchedRef.current = false;
    };
  }, [type, id]);

  // Apply coupon if provided in URL
  useEffect(() => {
    // Prevent double execution and ensure product is loaded
    if (!couponCode || !product) return;
    if (couponAppliedRef.current) return;
    couponAppliedRef.current = true;
    
    const applyCoupon = async () => {
      try {
        const response = await fetch(`${API_URL}/api/coupons/validate`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            code: couponCode,
            product_id: product.id,
            product_type: product.type
          }),
          cache: 'no-store'
        });
        
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('Failed to parse coupon response:', parseError);
          return;
        }
        
        if (data.valid) {
          setAppliedCoupon({
            code: couponCode,
            discount_type: data.discount_type,
            discount_value: data.discount_value,
            original_price: data.original_price,
            discounted_price: data.discounted_price
          });
        }
      } catch (err) {
        console.error('Failed to apply coupon:', err);
      }
    };

    applyCoupon();
    
    // Reset ref on unmount
    return () => {
      couponAppliedRef.current = false;
    };
  }, [couponCode, product]);

  const formatPeriod = (period) => {
    switch (period) {
      case 'month': return '/mo';
      case '3months': return '/3mo';
      case '6months': return '/6mo';
      case 'year': return '/yr';
      case 'one-time': return '';
      default: return period ? `/${period}` : '';
    }
  };

  const handlePurchase = async () => {
    if (!isLoggedIn) {
      // Not logged in - go to login page
      if (product.type === 'membership') {
        navigate(`/login?coupon=${couponCode}&product=${id}&type=membership`);
      } else {
        navigate(`/login?coupon=${couponCode}&product=${id}&type=course`);
      }
      return;
    }

    // User is logged in - proceed to checkout
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const endpoint = product.type === 'membership' 
        ? `${API_URL}/api/membership/upgrade`
        : `${API_URL}/api/enrollment/enroll`;
      
      const body = product.type === 'membership'
        ? { 
            membership_id: product.id, 
            coupon_code: couponCode || undefined,
            origin_url: window.location.origin
          }
        : { 
            course_id: product.id, 
            coupon_code: couponCode || undefined,
            origin_url: window.location.origin
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="purchase-page">
        <div className="purchase-loading">
          <Loader2 size={40} className="spinner" />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="purchase-page">
        <div className="purchase-error">
          <h2>Product Not Found</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="back-home-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const displayPrice = appliedCoupon ? appliedCoupon.discounted_price : product.price;
  const originalPrice = appliedCoupon ? appliedCoupon.original_price : null;

  return (
    <div className="purchase-page">
      <nav className="purchase-nav">
        <div className="nav-brand" onClick={() => navigate('/')}>
          <span className="brand-text">HyperDrive</span>
          <span className="brand-tagline">Trading Academy</span>
        </div>
      </nav>

      <div className="purchase-container">
        <div className="purchase-card">
          <div className="purchase-badge">
            {product.type === 'membership' ? 'MEMBERSHIP' : 'COURSE'}
          </div>
          
          <h1 className="purchase-title">{product.name || product.title}</h1>
          
          {product.description && (
            <p className="purchase-description">{product.description}</p>
          )}

          {appliedCoupon && (
            <div className="coupon-applied-banner">
              <Tag size={18} />
              <span className="coupon-code">{appliedCoupon.code}</span>
              <span className="coupon-discount">
                {appliedCoupon.discount_type === 'percentage' 
                  ? `${appliedCoupon.discount_value}% OFF` 
                  : `$${appliedCoupon.discount_value} OFF`}
              </span>
            </div>
          )}

          <div className="purchase-price">
            {originalPrice && (
              <span className="price-original">${originalPrice}</span>
            )}
            <span className="price-amount">${displayPrice}</span>
            <span className="price-period">
              {product.type === 'membership' ? formatPeriod(product.period) : ' One-Time'}
            </span>
          </div>

          {product.features && product.features.length > 0 && (
            <ul className="purchase-features">
              {product.features.map((feature, idx) => (
                <li key={idx}>
                  <CheckCircle size={18} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}

          <button 
            className="purchase-cta" 
            onClick={handlePurchase}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? (
              <>
                <Loader2 size={20} className="spinner" />
                Processing...
              </>
            ) : (
              <>
                {isLoggedIn ? 'Proceed to Checkout' : 'Get Started'}
                <ArrowRight size={20} />
              </>
            )}
          </button>

          <p className="purchase-note">
            {isLoggedIn 
              ? "You'll be redirected to Stripe to complete your purchase securely."
              : "You'll be redirected to create an account or login to complete your purchase."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PurchasePage;
